import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useTransactionsFeesHunterCardEarnings } from "Api/Queries/Transactions/useTransactionsFeesHunterCardEarnings";
import { useTransactionsFeesHunterEarnings } from "Api/Queries/Transactions/useTransactionsFeesHunterEarnings";
import { BaseTransactionFees } from "Components/Dashboard/TransactionFees/BaseTransactionFees";
import { InfoBox } from "Components/Shared/InfoBox/InfoBox";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { SettlementDownloadSection } from "Components/Transactions/UserTransactionFees/SettlementDownloadSection";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { isSettlementUserRole } from "Types/settlements";
import { BankAccountStatus } from "Infrastructure/Api/Api";
import { getRegionCurrency } from "Utils/CurrencyUtils";
import { endOfMonth, startOfMonth } from "date-fns";
import { Resources, useResource } from "Translations/Resources";
import { Stack, Typography } from "@mui/material";

export const HunterTransactionFees = () => {
  const { t } = useResource();
  const dateFrom = startOfMonth(new Date()).toISOString();
  const dateTo = endOfMonth(new Date()).toISOString();
  const { data: hunterEarnings, isLoading } = useTransactionsFeesHunterEarnings(
    dateFrom,
    dateTo,
  );
  const { data: cardEarnings } = useTransactionsFeesHunterCardEarnings();

  const { data: bankAccounts } = useBankingAccounts();
  const currentUser = useLoggedInUser();
  const userRole = useCurrentUserRole();

  if (!currentUser?.partyPublicID || !isSettlementUserRole(userRole)) {
    return null;
  }

  const hasActiveBankAccount = bankAccounts?.items?.some(
    ({ status }) => status === BankAccountStatus.Active,
  );

  return (
    <PrimaryPaper>
      <Stack gap={2} my={1}>
        {bankAccounts !== undefined && !hasActiveBankAccount && (
          <InfoBox
            variant="warning"
            content={t(
              Resources.Hunter.Dashboard.MissingLicenseHolderBankAccount,
            )}
          />
        )}
        <BaseTransactionFees
          amounts={hunterEarnings?.totalFees ?? []}
          emptyStateCurrency={
            currentUser.region
              ? getRegionCurrency(currentUser.region)
              : undefined
          }
          isLoading={isLoading}
          title={t(Resources.Components.Settlements.TotalEarnings)}
          colorVariant="success"
        />
        {/* The tile appears only once the module answers available — no skeleton, so module-off
            environments never flash a placeholder that then vanishes. */}
        {cardEarnings?.isAvailable && (
          <BaseTransactionFees
            // One entry per currency, exactly like the direct-fee figure above — the API labels
            // the money, the tile never assumes a currency.
            amounts={cardEarnings.currentPeriodAmounts}
            emptyStateCurrency={
              currentUser.region
                ? getRegionCurrency(currentUser.region)
                : undefined
            }
            isLoading={false}
            title={t(Resources.Components.Settlements.CardEarnings)}
            colorVariant="success"
          />
        )}
        <Stack gap={1}>
          <Typography>
            {t(Resources.Components.Settlements.SettlementDownloadButton.Title)}
          </Typography>
          <SettlementDownloadSection
            partyPublicID={currentUser.partyPublicID}
            userRole={userRole}
          />
        </Stack>
      </Stack>
    </PrimaryPaper>
  );
};
